import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 把 single_select 的引用选项从「页面首屏全量解析」改为「属性面板出现时按 action 懒加载」。
 *
 * 触发时机：用户选中节点、属性面板准备渲染 single_select 时，
 * 检查该 action 是否有 source 且尚未加载，再通过 setProps({optionRequest}) 发出请求。
 * 响应由外部 Dash callback 以 optionResponse（INPUT）写回。
 *
 * 状态机（方案 §6）：
 *   - runtimeOptions: Record<action, OptionItem[]>  已成功加载（含「成功为空」的 []）
 *   - loadingActions: Set<action>                    正在加载（仅用于 UI 展示）
 *   - optionErrors:   Record<action, string>         加载失败信息（仅用于 UI 展示 + 可重试）
 *   - statusRef:      Record<action, "pending"|"error">  同步守卫（ref，避免 setState 异步导致的重复请求死循环）
 *   - pendingIdRef:   Record<action, requestId>      用于拒绝过期响应
 *   - requestSeqRef:  number                         请求 ID 单调递增
 *
 * 关键：请求去重/防重入用 **同步 ref**（statusRef）而不是异步 state，
 * 否则 setProps 触发重渲染时 React 尚未 flush setLoadingActions，effect 用旧 state 判空失败 → 重复请求 → 无限循环。
 *
 * 选项优先级（方案 §5.1）：
 *   runtimeOptions[action] > dynamicOptions[action] > Schema 静态 component.options
 */

/** 静态 OptionItem({value,name}) → mapping 请求的 values({label,value})。 */
function toLabelValue(options) {
  return (options ?? []).map((o) => ({ label: o.name, value: o.value }));
}

export function useLazyOptions({
  selectedNode,
  schema,
  dynamicOptions,
  optionResponse,
  setProps,
}) {
  const [runtimeOptions, setRuntimeOptions] = useState({});
  const [loadingActions, setLoadingActions] = useState(() => new Set());
  const [optionErrors, setOptionErrors] = useState({});
  const statusRef = useRef({}); // action -> "pending" | "error"（同步守卫）
  const pendingIdRef = useRef({}); // action -> requestId
  const requestSeqRef = useRef(0);

  const action = selectedNode?.action ?? null;
  const component = action ? schema?.actions?.[action]?.components?.[0] : null;

  const requestOption = useCallback(
    (act, comp) => {
      // 同步去重：正在请求时不再重复发（ref 写立即生效，不受 setState 批处理影响）
      if (statusRef.current[act] === "pending") return;
      requestSeqRef.current += 1;
      const requestId = `${act}-${requestSeqRef.current}`;
      statusRef.current[act] = "pending";
      pendingIdRef.current[act] = requestId;
      setLoadingActions((prev) => {
        const next = new Set(prev);
        next.add(act);
        return next;
      });
      setOptionErrors((prev) => {
        const { [act]: _removed, ...rest } = prev;
        return rest;
      });
      setProps?.({
        optionRequest: {
          requestId,
          action: act,
          sources: comp?.source ?? [],
          values: toLabelValue(comp?.options),
          triggeredAt: Date.now(),
        },
      });
    },
    [setProps]
  );

  // 选中节点 → 需要时发出 optionRequest
  useEffect(() => {
    if (!component) return;
    if (component.type !== "single_select") return;
    if (!Array.isArray(component.source) || component.source.length === 0) return;
    if (Object.prototype.hasOwnProperty.call(runtimeOptions, action)) return;
    if (dynamicOptions && Object.prototype.hasOwnProperty.call(dynamicOptions, action)) return;
    // 失败后不自动重试，等用户显式点「重试」再 requestOption
    if (statusRef.current[action] === "error") return;
    requestOption(action, component); // 内部用 statusRef 同步去重「pending」
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, component, runtimeOptions, dynamicOptions, requestOption]);

  // optionResponse → 按 requestId + action 合并，拒绝过期响应
  useEffect(() => {
    if (!optionResponse) return;
    const { action: respAction, status, options, requestId } = optionResponse;
    const pendingId = pendingIdRef.current[respAction];
    // 未知或过期请求：忽略，防止旧响应覆盖新请求状态
    if (pendingId === undefined || pendingId !== requestId) return;
    delete pendingIdRef.current[respAction];
    if (status === "success") {
      delete statusRef.current[respAction];
      setRuntimeOptions((prev) => ({ ...prev, [respAction]: options ?? [] }));
    } else {
      statusRef.current[respAction] = "error";
    }
    setLoadingActions((prev) => {
      const next = new Set(prev);
      next.delete(respAction);
      return next;
    });
    setOptionErrors((prev) => {
      if (status === "success") {
        const { [respAction]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [respAction]: optionResponse.message || "加载失败" };
    });
  }, [optionResponse]);

  // 最终选项：runtimeOptions > dynamicOptions > 静态 options
  const getOptions = useCallback(
    (act, comp) => {
      if (Object.prototype.hasOwnProperty.call(runtimeOptions, act)) {
        return runtimeOptions[act];
      }
      if (dynamicOptions && Object.prototype.hasOwnProperty.call(dynamicOptions, act)) {
        return dynamicOptions[act];
      }
      return comp?.options ?? [];
    },
    [runtimeOptions, dynamicOptions]
  );

  return {
    runtimeOptions,
    loadingActions,
    optionErrors,
    getOptions,
    requestOption,
  };
}
